import {
    FC,
    ReactElement,
    ReactNode,
    useCallback,
    useMemo,
    useState,
} from 'react'
import { Channel, ChannelFilters, StreamChat } from 'stream-chat'
import {
    ChannelList,
    Chat,
} from 'stream-chat-react'

import { LoadingSpinner, ZeroState } from '@/components'
import { EnableNotificationsBanner } from '@/compositions'
import { ChatUser } from '@/entities/ChatUser/ChatUser'
import { streamI18n } from '@/helpers/streamI18n'
import { trans } from '@/helpers/trans'

import {
    ConversationListItem,
    DirectMessagesEmptyState,
    NewConversationButton,
    NewConversationModal,
} from './subcomponents'

import './MessagesContainer.scss'

// ChannelList renders nothing at all when querying the channels fails, unless given this
const ConnectionErrorState: FC = (): ReactElement => (
    <ZeroState text={trans('pages.messages.connectionError')} />
)

interface MessagesContainerProps {
    client: StreamChat | null
    hasConnectionError: boolean
    chatUsers: ChatUser[]
    isLoadingChatUsers: boolean
}

export const MessagesContainer: FC<MessagesContainerProps> = ({
    client,
    hasConnectionError,
    chatUsers,
    isLoadingChatUsers,
}): ReactElement => {
    const [isNewChatOpen, setIsNewChatOpen] = useState(false)

    const openNewChat = useCallback(() => setIsNewChatOpen(true), [])
    const closeNewChat = useCallback(() => setIsNewChatOpen(false), [])

    const filters = useMemo<ChannelFilters | undefined>(() => (
        client?.userID ? {
            members: { $in: [client.userID] },
            type: { $in: ['team', 'messaging'] },
        } : undefined
    ), [client])

    const EmptyStateIndicator = useCallback((): ReactElement => (
        <DirectMessagesEmptyState onStartChat={openNewChat} />
    ), [openNewChat])

    const renderChannels = useCallback((
        loadedChannels: Channel[],
        renderChannel: (channel: Channel) => ReactNode,
    ): ReactNode => {
        const groups = loadedChannels.filter(channel => channel.type === 'team')
        const directMessages = loadedChannels.filter(channel => channel.type === 'messaging')

        return (
            <>
                {/* Only rendered once there are conversations; ChannelList shows the EmptyStateIndicator otherwise */}
                <EnableNotificationsBanner className="messages-container__enable-notifications-banner" />

                <h1 className="messages-container__title">
                    {trans('nav.communication.messages.label')}
                </h1>

                {!! groups.length && (
                    <section className="messages-container__section" aria-labelledby="groupMessagesHeading">
                        <h2 id="groupMessagesHeading" className="messages-container__heading">
                            {trans('pages.messages.groups')}
                        </h2>
                        {groups.map(renderChannel)}
                    </section>
                )}

                <section className="messages-container__section" aria-labelledby="directMessagesHeading">
                    <h2 id="directMessagesHeading" className="messages-container__heading">
                        {trans('pages.messages.directMessages')}
                    </h2>
                    {directMessages.length ? directMessages.map(renderChannel) : (
                        <DirectMessagesEmptyState onStartChat={openNewChat} />
                    )}
                </section>
            </>
        )
    }, [openNewChat])

    if (hasConnectionError) {
        return (
            <div className="messages-container">
                <ConnectionErrorState />
            </div>
        )
    }

    return (
        <div className="messages-container">
            {! client || ! filters ? (
                <LoadingSpinner />
            ) : (
                <>
                    <Chat client={client} i18nInstance={streamI18n} theme="str-chat__theme-light">
                        <ChannelList
                            allowNewMessagesFromUnfilteredChannels={false}
                            filters={filters}
                            sort={{ last_message_at: -1 }}
                            options={{ state: true, watch: true, presence: true }}
                            Preview={ConversationListItem}
                            renderChannels={renderChannels}
                            EmptyStateIndicator={EmptyStateIndicator}
                            LoadingErrorIndicator={ConnectionErrorState}
                            setActiveChannelOnMount={false}
                        />
                    </Chat>

                    <NewConversationButton onClick={openNewChat} />

                    {isNewChatOpen && (
                        <NewConversationModal
                            chatUsers={chatUsers}
                            isLoading={isLoadingChatUsers}
                            onClose={closeNewChat}
                        />
                    )}
                </>
            )}
        </div>
    )
}
